import React, { useState, useEffect, useCallback } from 'react';
import { db } from './firebase';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, increment, deleteDoc } from "firebase/firestore";

export default function ReservasEdificio({ usuarioLogueado }) {
  const [reservas, setReservas] = useState([]);
  const [areasSociales, setAreasSociales] = useState([]);
  const [edificioData, setEdificioData] = useState({});
  const [cargando, setCargando] = useState(true);

  // 🔑 Estados para el Modo Supervisor / Contraseña
  const [esSupervisor, setEsSupervisor] = useState(false);
  const [mostrarPromptSupervisor, setMostrarPromptSupervisor] = useState(false);
  const [passwordSupervisorInput, setPasswordSupervisorInput] = useState('');

  const edificioId = usuarioLogueado?.edificioId;

  // 📥 Carga directa y exclusiva de las colecciones y datos del edificio
  const cargarDatosEdificio = useCallback(async () => {
    if (!edificioId) return;
    try {
      setCargando(true);
      const [edifSnap, snapAreas, snapRes] = await Promise.all([
        getDoc(doc(db, 'edificios_mmdl', edificioId)),
        getDocs(collection(db, `areas_${edificioId}`)),
        getDocs(collection(db, `reservas_${edificioId}`))
      ]);

      if (edifSnap.exists()) {
        setEdificioData(edifSnap.data());
      }
      setAreasSociales(snapAreas.docs.map(d => ({ id: d.id, ...d.data() })));
      setReservas(snapRes.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error al cargar datos del edificio:", error);
    } finally {
      setCargando(false);
    }
  }, [edificioId]);

  useEffect(() => {
    cargarDatosEdificio();
  }, [cargarDatosEdificio]);

  // 🧮 Utilidad para limpiar y convertir valores a número
  const obtenerValorNumerico = (valorStr) => {
    if (valorStr === undefined || valorStr === null || valorStr === '') return 0;
    if (typeof valorStr === 'number') return valorStr;
    const limpio = valorStr.toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(limpio);
    return isNaN(num) ? 0 : num;
  };

  // 🔐 Validar contraseña fija de supervisor
  const validarPasswordSupervisor = (e) => {
    e.preventDefault();
    const passwordSupervisorFija = '20MmDLS*UPERVI20';

    if (passwordSupervisorInput.trim() === passwordSupervisorFija) {
      setEsSupervisor(true);
      setMostrarPromptSupervisor(false);
      setPasswordSupervisorInput('');
      alert("✅ ¡Modo Supervisor activado con éxito!");
    } else {
      alert("❌ Contraseña incorrecta.");
    }
  };

  // 🔄 Función para retornar o anular la devolución de garantía (Solo Supervisor)
  const retornarGarantiaGrupo = async (grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const yaDevuelta = grupo.estadoGarantia === 'Devuelta';

    const mensaje = yaDevuelta
      ? `¿Deseas ANULAR la devolución de garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento}? El monto volverá a sumarse al total recaudado.`
      : `¿Estás seguro de registrar la devolución de la garantía por S/ ${valorGarantia.toFixed(2)} para el Dpto. ${grupo.departamento}? Esto se descontará del total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        for (let id of grupo.ids) {
          await updateDoc(doc(db, `reservas_${edificioId}`, id), {
            estadoGarantia: yaDevuelta ? '' : 'Devuelta'
          });
        }

        await cargarDatosEdificio();

        alert(yaDevuelta ? "✅ Devolución anulada. El fondo ha sido restaurado." : "✅ Garantía devuelta y descontada del total con éxito.");
      } catch (error) {
        console.error("Error al modificar garantía:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // ❌ Función para alternar si la garantía no fue pagada (Solo Supervisor)
  const toggleGarantiaNoPagada = async (grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);

    if (valorGarantia <= 0) {
      alert("⚠️ Esta reserva no cuenta con un monto de garantía aplicable.");
      return;
    }

    const noPagadaActual = grupo.estadoGarantia === 'No pagada';

    const mensaje = noPagadaActual
      ? `¿Deseas marcar la garantía como PAGADA para el Dpto. ${grupo.departamento}? El monto volverá a sumarse al total recaudado.`
      : `¿Deseas marcar la garantía como NO PAGADA para el Dpto. ${grupo.departamento}? El monto dejará de sumarse al total recaudado.`;

    if (window.confirm(mensaje)) {
      try {
        const nuevoEstado = noPagadaActual ? '' : 'No pagada';

        for (let id of grupo.ids) {
          await updateDoc(doc(db, `reservas_${edificioId}`, id), {
            estadoGarantia: nuevoEstado
          });
        }

        await cargarDatosEdificio();

        alert(noPagadaActual ? "✅ Garantía marcada como pagada. Se ha vuelto a sumar al total." : "✅ Garantía marcada como no pagada. Se excluyó del total recaudado.");
      } catch (error) {
        console.error("Error al actualizar estado de garantía:", error);
        alert("❌ Hubo un error al actualizar el estado de la garantía.");
      }
    }
  };

  // 🗑️ Función para liberar / cancelar reservas si el voucher no es válido (Solo Supervisor)
  const liberarGrupo = async (ids) => {
    if (window.confirm("⚠️ ¿Está seguro de liberar estos horarios? Se eliminará la reserva y los horarios quedarán libres nuevamente.")) {
      try {
        for (let id of ids) {
          await deleteDoc(doc(db, `reservas_${edificioId}`, id));
        }
        await cargarDatosEdificio();
        alert("🗑️ Reserva(s) liberada(s) correctamente.");
      } catch (error) {
        console.error("Error al liberar reservas:", error);
        alert("❌ Hubo un error al intentar liberar los horarios.");
      }
    }
  };

  // 🔒 Función con límite estricto de 5 veces por reserva específica al día
  const abrirArchivoConLimitePorReserva = async (fileUrl, reservaId) => {
    if (!fileUrl) {
      alert("⚠️ El archivo aún no está disponible.");
      return;
    }

    const hoy = new Date().toISOString().split('T')[0];
    const registroId = `reserva_${edificioId}_${reservaId}_${hoy}`;
    const docRef = doc(db, "control_vistas_archivos", registroId);

    try {
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const datos = docSnap.data();
        if (datos.contador >= 5) {
          alert("❌ Has alcanzado el límite máximo de 5 visualizaciones permitidas para esta reserva el día de hoy.");
          return;
        }
        await updateDoc(docRef, { contador: increment(1) });
      } else {
        await setDoc(docRef, { edificioId, reservaId, fecha: hoy, contador: 1 });
      }

      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error("Error al verificar el límite de visualización:", error);
      alert("Ocurrió un error al verificar el acceso al documento.");
    }
  };

  // 📦 Agrupación lógica de reservas múltiples por lote para evitar duplicidad
  const reservasAgrupadasMap = {};
  reservas.forEach(r => {
    const key = `${r.areaId}_${r.fecha}_${r.departamento}_${r.voucherUrl || ''}`;
    if (!reservasAgrupadasMap[key]) {
      reservasAgrupadasMap[key] = {
        ids: [r.id],
        areaId: r.areaId,
        departamento: r.departamento,
        fecha: r.fecha,
        horarios: [r.horario],
        voucherUrl: r.voucherUrl,
        voucherGarantiaUrl: r.voucherGarantiaUrl,
        estadoGarantia: r.estadoGarantia,
        estadoPago: r.estadoPago
      };
    } else {
      reservasAgrupadasMap[key].ids.push(r.id);
      reservasAgrupadasMap[key].horarios.push(r.horario);
      if (r.voucherGarantiaUrl && !reservasAgrupadasMap[key].voucherGarantiaUrl) {
        reservasAgrupadasMap[key].voucherGarantiaUrl = r.voucherGarantiaUrl;
        reservasAgrupadasMap[key].estadoGarantia = r.estadoGarantia;
      }
    }
  });

  const listaReservasAgrupadas = Object.values(reservasAgrupadasMap).map(grupo => {
    const hoursList = grupo.horarios.map(h => parseInt(h.split(':')[0]) || 0);
    const hasEvening = hoursList.some(h => h >= 18);

    const horariosOrdenados = [...grupo.horarios].sort((a, b) => {
      const hA_raw = parseInt(a.split(':')[0]) || 0;
      const hB_raw = parseInt(b.split(':')[0]) || 0;

      let hA = hA_raw;
      let hB = hB_raw;

      if (hasEvening) {
        if (hA < 6) hA += 24;
        if (hB < 6) hB += 24;
      }

      return hA - hB;
    });

    let horarioMostrado = '';
    if (horariosOrdenados.length === 1) {
      horarioMostrado = horariosOrdenados[0];
    } else {
      const primerHorario = horariosOrdenados[0];
      const ultimoHorario = horariosOrdenados[horariosOrdenados.length - 1];
      const partesPrimero = primerHorario.split('-');
      const partesUltimo = ultimoHorario.split('-');
      if (partesPrimero.length === 2 && partesUltimo.length === 2) {
        horarioMostrado = `${partesPrimero[0].trim()} - ${partesUltimo[1].trim()}`;
      } else {
        horarioMostrado = `${primerHorario} al ${ultimoHorario}`;
      }
    }
    return {
      ...grupo,
      horarioMostrado
    };
  });

  // 💰 Cálculos financieros limpios del edificio (Garantías Netas)
  const montoInicialBase = obtenerValorNumerico(edificioData?.montoInicialReservas || 0);
  
  const totalAlquileres = listaReservasAgrupadas.reduce((acc, grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    return acc + obtenerValorNumerico(area?.costo);
  }, 0);

  const totalGarantiasNeto = listaReservasAgrupadas.reduce((acc, grupo) => {
    const area = areasSociales.find(a => a.id === grupo.areaId);
    const valorGarantia = obtenerValorNumerico(area?.garantia);
    if (grupo.estadoGarantia === 'No pagada' || grupo.estadoGarantia === 'Devuelta') {
      return acc;
    }
    return acc + valorGarantia;
  }, 0);

  const totalRecaudadoBruto = totalAlquileres + totalGarantiasNeto;
  const granTotalAcumulado = montoInicialBase + totalRecaudadoBruto;

  if (cargando) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>⏳ Cargando portal de conserjería...</div>;
  }

  return (
    <div style={{ maxWidth: '1000px', width: '100%', margin: '20px auto', padding: '0 15px', textAlign: 'left', boxSizing: 'border-box' }}>
      
      {/* Tarjeta de Bienvenida y Control de Supervisor */}
      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '25px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <span style={{ backgroundColor: '#2563eb', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>Portal de Personal / Conserjería</span>
          <h2 style={{ margin: '10px 0 5px 0', color: '#1e40af' }}>Gestión y Control de Reservas — Edificio {usuarioLogueado.edificioNombre}</h2>
          <p style={{ margin: 0, color: '#1d4ed8', fontSize: '0.95rem' }}>Visualiza las reservas de los propietarios, revisa sus comprobantes y consulta el estado de caja.</p>
        </div>

        <div>
          {esSupervisor ? (
            <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', padding: '8px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: '#166534', fontWeight: 'bold', fontSize: '0.85rem' }}>🔓 Modo Supervisor Activo</span>
              <button 
                onClick={() => setEsSupervisor(false)} 
                style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Bloquear
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setMostrarPromptSupervisor(true)}
              style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              🔐 Activar Modo Supervisor
            </button>
          )}
        </div>
      </div>

      {/* Modal / Prompt de Contraseña de Supervisor */}
      {mostrarPromptSupervisor && (
        <div style={{ backgroundColor: '#fdf4ff', border: '2px solid #f0abfc', borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#86198f' }}>🔑 Ingresar Contraseña de Supervisor</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.82rem', color: '#a21caf' }}>Introduce la clave para desbloquear las opciones de devolución, gestión de garantías y liberación de horarios.</p>
          <form onSubmit={validarPasswordSupervisor} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input 
              type="password" 
              placeholder="Contraseña..." 
              value={passwordSupervisorInput} 
              onChange={e => setPasswordSupervisorInput(e.target.value)} 
              style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #f0abfc', flex: '1', minWidth: '150px' }} 
              required 
            />
            <button type="submit" style={{ backgroundColor: '#a21caf', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}>Desbloquear</button>
            <button type="button" onClick={() => setMostrarPromptSupervisor(false)} style={{ backgroundColor: '#94a3b8', color: 'white', border: 'none', padding: '7px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' }}>Cancelar</button>
          </form>
        </div>
      )}

      {/* 📊 TARJETAS DE RESUMEN Y CONTROL DE FONDO */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px', marginBottom: '25px' }}>
        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 'bold' }}>📥 Monto Inicial / Fondo Base:</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#0f172a' }}>S/ {montoInicialBase.toFixed(2)}</h3>
        </div>
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#166534', fontWeight: 'bold' }}>💰 Recaudado (Alquileres + Garantías Netas):</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#15803d' }}>S/ {totalRecaudadoBruto.toFixed(2)}</h3>
        </div>
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', padding: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>📈 Gran Total Acumulado en Caja:</span>
          <h3 style={{ margin: '5px 0 0 0', color: '#1e40af' }}>S/ {granTotalAcumulado.toFixed(2)}</h3>
        </div>
      </div>

      {/* Listado de Reservas Agrupadas */}
      <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '25px' }}>
        <h3 style={{ marginTop: 0, color: '#1e293b' }}>📅 Horarios Bloqueados y Reservas de Propietarios</h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '20px' }}>Revisa los comprobantes de pago y estado de garantías desde conserjería.</p>
        
        <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <table className="tabla-propietarios">
            <thead>
              <tr>
                <th>Área Social</th>
                <th>Dpto Solicitante</th>
                <th>Fecha</th>
                <th>Horario</th>
                <th>Estado / Voucher Alquiler</th>
                <th>Voucher / Estado Garantía</th>
                {esSupervisor && <th>Acción Supervisor</th>}
              </tr>
            </thead>
            <tbody>
              {listaReservasAgrupadas.length === 0 ? (
                <tr><td colSpan={esSupervisor ? "7" : "6"} style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No hay reservas registradas en este edificio.</td></tr>
              ) : (
                listaReservasAgrupadas.map((grupo, idx) => {
                  const area = areasSociales.find(a => a.id === grupo.areaId);
                  const garantiaNum = obtenerValorNumerico(area?.garantia);
                  const reservaUnicaId = grupo.ids[0];

                  return (
                    <tr key={idx}>
                      <td><strong>{area ? area.nombre : 'Área'}</strong></td>
                      <td>Dpto. {grupo.departamento}</td>
                      <td>{grupo.fecha}</td>
                      <td><span style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold' }}>🔒 {grupo.horarioMostrado}</span></td>
                      
                      {/* Columna Voucher de Alquiler */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: grupo.voucherUrl ? '#1d4ed8' : '#15803d' }}>
                            {grupo.estadoPago || (grupo.voucherUrl ? 'Voucher enviado' : 'Gratis / Sin costo')}
                          </span>
                          {grupo.voucherUrl ? (
                            <button 
                              type="button"
                              onClick={() => abrirArchivoConLimitePorReserva(grupo.voucherUrl, reservaUnicaId)}
                              style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              👁️ Ver Voucher ↗
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>No requiere voucher</span>
                          )}
                        </div>
                      </td>

                      {/* Columna Voucher y Estado de Garantía */}
                      <td>
                        {garantiaNum > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                            {grupo.voucherGarantiaUrl ? (
                              <button 
                                type="button"
                                onClick={() => abrirArchivoConLimitePorReserva(grupo.voucherGarantiaUrl, reservaUnicaId)}
                                style={{ backgroundColor: '#d97706', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                              >
                                👁️ Ver Garantía ↗
                              </button>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>Sin voucher de garantía</span>
                            )}

                            {grupo.estadoGarantia === 'Devuelta' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                  ✅ Garantía Devuelta
                                </span>
                                {esSupervisor && (
                                  <button 
                                    type="button"
                                    onClick={() => retornarGarantiaGrupo(grupo)}
                                    style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                  >
                                    ↩️ Anular
                                  </button>
                                )}
                              </div>
                            ) : grupo.estadoGarantia === 'No pagada' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                  S/ {garantiaNum.toFixed(2)}
                                </span>
                                <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                  ❌ No Pagada
                                </span>
                                {esSupervisor && (
                                  <button 
                                    type="button"
                                    onClick={() => toggleGarantiaNoPagada(grupo)}
                                    style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                  >
                                    ✅ Marcar Pagada
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                <span style={{ color: '#475569', fontSize: '0.75rem' }}>Garantía Activa: <strong>S/ {garantiaNum.toFixed(2)}</strong></span>
                                {esSupervisor && (
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button 
                                      type="button"
                                      onClick={() => retornarGarantiaGrupo(grupo)}
                                      style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                      🔄 Devolver
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={() => toggleGarantiaNoPagada(grupo)}
                                      style={{ backgroundColor: '#b91c1c', color: 'white', border: 'none', padding: '3px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                      ❌ No Pagada
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '0.75rem' }}>No aplica</span>
                        )}
                      </td>

                      {/* Columna de Acción (Solo visible en modo supervisor) */}
                      {esSupervisor && (
                        <td>
                          <button 
                            onClick={() => liberarGrupo(grupo.ids)} 
                            style={{ backgroundColor: '#d9534f', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}
                            title="Liberar horarios (rechazar voucher o cancelar reserva)"
                          >
                            Liberar
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}